// src/components/admin/LoyaltyProgramEditor.jsx
import React, { useState, useEffect } from 'react';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../utils/firebase';
import '../../styles/loyalty-program-editor.css';

// Default tiers for new salons
const DEFAULT_TIERS = [
  { name: 'Bronze', minPoints: 0, maxPoints: 499 },
  { name: 'Silver', minPoints: 500, maxPoints: 999 },
  { name: 'Gold', minPoints: 1000, maxPoints: 1999 },
  { name: 'Platinum', minPoints: 2000, maxPoints: null } // null means unlimited
];

function LoyaltyProgramEditor({ salonId, onUpdate }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedMessage, setSavedMessage] = useState('');
  const [tierErrors, setTierErrors] = useState([]);

  // Loyalty program settings
  const [settings, setSettings] = useState({
    // Points earning rules
    pointsPerCurrency: 10,
    currencyPerPoint: 10,

    // Points redemption value
    pointValue: 0.1,

    // Points expiration
    pointsExpire: false,
    expirationMonths: 12,

    // Loyalty tiers
    tiers: DEFAULT_TIERS,

    // Points bonus periods
    pointsBonus: {
      birthday: {
        enabled: false,
        multiplier: 2
      },
      custom: {
        enabled: false,
        startDate: '',
        endDate: '',
        multiplier: 2
      }
    },

    // Status
    isActive: true
  });

  // Load existing settings on mount
  useEffect(() => {
    loadSettings();
  }, [salonId]);

  const loadSettings = async () => {
    try {
      setLoading(true);

      const salonRef = doc(db, 'salons', salonId);
      const salonDoc = await getDoc(salonRef);

      if (salonDoc.exists()) {
        const data = salonDoc.data();
        if (data.loyaltyProgram) {
          // Firestore can return tiers as a plain object (map) rather than an array,
          // and individual tier entries may have missing/undefined numeric fields.
          // Normalise everything so .map() and .toLocaleString() never crash.
          let rawTiers = data.loyaltyProgram.tiers;
          if (!rawTiers) {
            rawTiers = DEFAULT_TIERS;
          } else {
            if (!Array.isArray(rawTiers)) {
              rawTiers = Object.values(rawTiers);
            }
            rawTiers = rawTiers.map((tier, i) => ({
              name: tier.name || `Tier ${i + 1}`,
              minPoints: typeof tier.minPoints === 'number' ? tier.minPoints : (parseInt(tier.minPoints) || 0),
              maxPoints: tier.maxPoints === null || tier.maxPoints === undefined
                ? (i === rawTiers.length - 1 ? null : 0)
                : (typeof tier.maxPoints === 'number' ? tier.maxPoints : parseInt(tier.maxPoints) || 0),
            }));
          }

          setSettings(prev => ({
            ...prev,
            ...data.loyaltyProgram,
            // Always use the normalised array, not the raw Firestore value
            tiers: rawTiers,
            // Merge pointsBonus deeply so missing fields use defaults
            pointsBonus: {
              birthday: {
                enabled: false,
                multiplier: 2,
                ...(data.loyaltyProgram.pointsBonus?.birthday || {})
              },
              custom: {
                enabled: false,
                startDate: '',
                endDate: '',
                multiplier: 2,
                ...(data.loyaltyProgram.pointsBonus?.custom || {})
              }
            }
          }));
        }
      }
    } catch (error) {
      console.error('Error loading loyalty program settings:', error);
    } finally {
      setLoading(false);
    }
  };

  // Validate tier point ranges for overlaps
  const validateTiers = (tiers) => {
    const errors = [];

    for (let i = 0; i < tiers.length; i++) {
      const tier = tiers[i];

      // Check if tier name is empty
      if (!tier.name || tier.name.trim() === '') {
        errors.push({ index: i, message: `Tier ${i + 1} name cannot be empty` });
      }

      // Check if minPoints is valid
      if (tier.minPoints < 0) {
        errors.push({ index: i, message: `${tier.name || `Tier ${i + 1}`}: Minimum points cannot be negative` });
      }

      // Check if maxPoints is valid (can be null for top tier)
      if (tier.maxPoints !== null && tier.maxPoints < tier.minPoints) {
        errors.push({ index: i, message: `${tier.name || `Tier ${i + 1}`}: Maximum points must be greater than or equal to minimum points` });
      }

      // Check for overlaps with other tiers
      for (let j = 0; j < tiers.length; j++) {
        if (i !== j) {
          const otherTier = tiers[j];

          // Check if ranges overlap
          const tierMax = tier.maxPoints === null ? Infinity : tier.maxPoints;
          const otherMax = otherTier.maxPoints === null ? Infinity : otherTier.maxPoints;

          // Two ranges [a, b] and [c, d] overlap if a <= d && c <= b
          if (tier.minPoints <= otherMax && otherTier.minPoints <= tierMax) {
            // Only report each overlap once (when i < j)
            if (i < j) {
              errors.push({
                index: i,
                message: `${tier.name || `Tier ${i + 1}`} (${tier.minPoints}-${tier.maxPoints === null ? '∞' : tier.maxPoints}) overlaps with ${otherTier.name || `Tier ${j + 1}`} (${otherTier.minPoints}-${otherTier.maxPoints === null ? '∞' : otherTier.maxPoints})`
              });
            }
          }
        }
      }
    }

    // Check for gaps between tiers (warning, not error)
    const sortedTiers = [...tiers].sort((a, b) => a.minPoints - b.minPoints);
    for (let i = 0; i < sortedTiers.length - 1; i++) {
      const currentMax = sortedTiers[i].maxPoints;
      const nextMin = sortedTiers[i + 1].minPoints;

      if (currentMax !== null && nextMin > currentMax + 1) {
        errors.push({
          index: tiers.indexOf(sortedTiers[i]),
          message: `Gap detected: Points ${currentMax + 1} to ${nextMin - 1} are not assigned to any tier`,
          isWarning: true
        });
      }
    }

    return errors;
  };

  const handleSave = async () => {
    // Validate tiers before saving
    const errors = validateTiers(settings.tiers);
    const actualErrors = errors.filter(e => !e.isWarning);

    if (actualErrors.length > 0) {
      setTierErrors(errors);
      setSavedMessage('Error: Please fix tier validation errors before saving.');
      setTimeout(() => setSavedMessage(''), 5000);
      return;
    }

    try {
      setSaving(true);

      const salonRef = doc(db, 'salons', salonId);
      const salonDoc = await getDoc(salonRef);

      if (salonDoc.exists()) {
        await updateDoc(salonRef, {
          loyaltyProgram: settings,
          updatedAt: new Date()
        });
      } else {
        // Salon document should already exist, but create if needed
        await setDoc(salonRef, {
          salonId: salonId,
          loyaltyProgram: settings,
          createdAt: new Date(),
          updatedAt: new Date()
        }, { merge: true });
      }

      setTierErrors([]);
      setSavedMessage('Loyalty program settings saved successfully!');
      setTimeout(() => setSavedMessage(''), 3000);

      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error('Error saving loyalty program settings:', error);
      setSavedMessage('Error saving settings. Please try again.');
      setTimeout(() => setSavedMessage(''), 3000);
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleBonusChange = (period, field, value) => {
    setSettings(prev => ({
      ...prev,
      pointsBonus: {
        ...prev.pointsBonus,
        [period]: {
          ...prev.pointsBonus[period],
          [field]: value
        }
      }
    }));
  };

  // Handle tier count change
  const handleTierCountChange = (count) => {
    const newCount = Math.max(1, Math.min(10, count)); // Allow 1-10 tiers
    const currentTiers = [...settings.tiers];

    if (newCount > currentTiers.length) {
      // Add new tiers
      const lastTier = currentTiers[currentTiers.length - 1];
      const lastMax = lastTier.maxPoints === null ? 2000 : lastTier.maxPoints;

      // Update last tier to have a maxPoints
      if (lastTier.maxPoints === null) {
        currentTiers[currentTiers.length - 1] = {
          ...lastTier,
          maxPoints: lastMax
        };
      }

      for (let i = currentTiers.length; i < newCount; i++) {
        const prevMax = currentTiers[i - 1].maxPoints || 999;
        currentTiers.push({
          name: `Tier ${i + 1}`,
          minPoints: prevMax + 1,
          maxPoints: i === newCount - 1 ? null : prevMax + 500
        });
      }
    } else if (newCount < currentTiers.length) {
      // Remove tiers from the end
      currentTiers.splice(newCount);
      // Make last tier unlimited
      if (currentTiers.length > 0) {
        currentTiers[currentTiers.length - 1].maxPoints = null;
      }
    }

    setSettings(prev => ({
      ...prev,
      tiers: currentTiers
    }));

    // Clear errors and revalidate
    setTierErrors(validateTiers(currentTiers));
  };

  // Handle tier name change
  const handleTierNameChange = (index, name) => {
    const newTiers = [...settings.tiers];
    newTiers[index] = { ...newTiers[index], name };

    setSettings(prev => ({
      ...prev,
      tiers: newTiers
    }));

    setTierErrors(validateTiers(newTiers));
  };

  // Handle tier point range change
  const handleTierRangeChange = (index, field, value) => {
    const newTiers = [...settings.tiers];
    const numValue = value === '' ? (field === 'maxPoints' ? null : 0) : parseInt(value);

    newTiers[index] = {
      ...newTiers[index],
      [field]: numValue
    };

    setSettings(prev => ({
      ...prev,
      tiers: newTiers
    }));

    setTierErrors(validateTiers(newTiers));
  };

  // Check if a specific tier has errors
  const getTierErrors = (index) => {
    return tierErrors.filter(e => e.index === index);
  };

  if (loading) {
    return (
      <div className="loyalty-program-editor">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading loyalty program settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="loyalty-program-editor">
      <div className="loyalty-header">
        <h2>Loyalty Program Settings</h2>
        <p className="loyalty-description">
          Configure how customers earn and redeem points for their transactions.
        </p>
      </div>

      {savedMessage && (
        <div className={`save-message ${savedMessage.includes('Error') ? 'error' : 'success'}`}>
          {savedMessage}
        </div>
      )}

      <div className="loyalty-sections">
        {/* Program Status */}
        <div className="loyalty-section">
          <h3>Program Status</h3>
          <div className="form-group toggle-group">
            <label className="toggle-label">
              <span>Loyalty Program Active</span>
              <div className="toggle-switch">
                <input
                  type="checkbox"
                  checked={settings.isActive}
                  onChange={(e) => handleInputChange('isActive', e.target.checked)}
                />
                <span className="toggle-slider"></span>
              </div>
            </label>
            <p className="field-help">
              When enabled, customers will earn and can redeem points for their transactions.
            </p>
          </div>
        </div>

        {/* Loyalty Tiers */}
        <div className="loyalty-section">
          <h3>Loyalty Tiers</h3>
          <p className="section-description">
            Define membership tiers based on customer point balances. Customers are automatically assigned to tiers based on their accumulated points.
          </p>

          {/* Number of Tiers */}
          <div className="tier-count-container">
            <div className="form-group inline-group">
              <label>Number of Tiers</label>
              <input
                type="number"
                min="1"
                max="10"
                value={settings.tiers.length}
                onChange={(e) => handleTierCountChange(parseInt(e.target.value) || 1)}
                className="small-input"
              />
              <span>(1-10 tiers)</span>
            </div>
          </div>

          {/* Tier Configuration */}
          <div className="tiers-list">
            {settings.tiers.map((tier, index) => {
              const errors = getTierErrors(index);
              const hasError = errors.some(e => !e.isWarning);
              const hasWarning = errors.some(e => e.isWarning);

              return (
                <div
                  key={index}
                  className={`tier-item ${hasError ? 'has-error' : ''} ${hasWarning && !hasError ? 'has-warning' : ''}`}
                >
                  <div className="tier-header">
                    <span className="tier-number">Tier {index + 1}</span>
                    {index === settings.tiers.length - 1 && (
                      <span className="tier-badge top-tier">Top Tier</span>
                    )}
                  </div>

                  <div className="tier-fields">
                    <div className="tier-field">
                      <label>Tier Name</label>
                      <input
                        type="text"
                        value={tier.name}
                        onChange={(e) => handleTierNameChange(index, e.target.value)}
                        placeholder="Enter tier name"
                        className={errors.some(e => e.message.includes('name')) ? 'input-error' : ''}
                      />
                    </div>

                    <div className="tier-field">
                      <label>Min Points</label>
                      <input
                        type="number"
                        min="0"
                        value={tier.minPoints}
                        onChange={(e) => handleTierRangeChange(index, 'minPoints', e.target.value)}
                        className={errors.some(e => e.message.includes('Minimum')) ? 'input-error' : ''}
                      />
                    </div>

                    <div className="tier-field">
                      <label>Max Points</label>
                      {index === settings.tiers.length - 1 ? (
                        <div className="unlimited-badge">Unlimited</div>
                      ) : (
                        <input
                          type="number"
                          min="0"
                          value={tier.maxPoints === null ? '' : tier.maxPoints}
                          onChange={(e) => handleTierRangeChange(index, 'maxPoints', e.target.value)}
                          placeholder="No limit"
                          className={errors.some(e => e.message.includes('Maximum')) ? 'input-error' : ''}
                        />
                      )}
                    </div>
                  </div>

                  {/* Tier-specific errors */}
                  {errors.length > 0 && (
                    <div className="tier-errors">
                      {errors.map((error, errIndex) => (
                        <div
                          key={errIndex}
                          className={`tier-error-message ${error.isWarning ? 'warning' : 'error'}`}
                        >
                          {error.isWarning ? '⚠️' : '❌'} {error.message}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Tier Preview */}
          <div className="tier-preview">
            <h4>Tier Summary</h4>
            <div className="tier-preview-list">
              {settings.tiers
                .slice()
                .sort((a, b) => a.minPoints - b.minPoints)
                .map((tier, index) => (
                  <div key={index} className="tier-preview-item">
                    <span className="tier-preview-name">{tier.name || `Tier ${index + 1}`}</span>
                    <span className="tier-preview-range">
                      {(tier.minPoints ?? 0).toLocaleString()} - {tier.maxPoints == null ? '∞' : (tier.maxPoints ?? 0).toLocaleString()} points
                    </span>
                  </div>
                ))}
            </div>
          </div>
        </div>

        {/* Points Earning Rules */}
        <div className="loyalty-section">
          <h3>Points Earning Rules</h3>
          <p className="section-description">
            Set how many points customers earn for every dollar spent.
          </p>

          <div className="earning-rule-container">
            <div className="form-group inline-group">
              <label>Earn</label>
              <input
                type="number"
                min="1"
                value={settings.pointsPerCurrency}
                onChange={(e) => handleInputChange('pointsPerCurrency', parseInt(e.target.value) || 10)}
                className="small-input"
              />
              <span>point(s) for every</span>
              <span className="currency-symbol">$</span>
              <input
                type="number"
                min="1"
                step="0.01"
                value={settings.currencyPerPoint}
                onChange={(e) => handleInputChange('currencyPerPoint', parseFloat(e.target.value) || 1)}
                className="small-input"
              />
              <span>spent</span>
            </div>
            <p className="field-help">
              Example: If set to "10 points for every $10 spent", a customer spending $50 will earn 50 points.
            </p>
          </div>
        </div>

        {/* Points Redemption Value */}
        <div className="loyalty-section">
          <h3>Points Redemption Value</h3>
          <p className="section-description">
            Set the monetary value of each point when customers redeem them.
          </p>

          <div className="redemption-rule-container">
            <div className="form-group inline-group">
              <label>1 point =</label>
              <span className="currency-symbol">$</span>
              <input
                type="number"
                min="0.01"
                step="0.01"
                value={settings.pointValue}
                onChange={(e) => handleInputChange('pointValue', parseFloat(e.target.value) || 0.01)}
                className="small-input"
              />
            </div>
            <p className="field-help">
              Example: If set to "$0.10", a customer with 100 points can redeem them for $10.00 off.
            </p>
          </div>

          {/* Preview calculation */}
          <div className="calculation-preview">
            <h4>Preview</h4>
            <div className="preview-grid">
              <div className="preview-item">
                <span className="preview-label">Points needed for $1 discount:</span>
                <span className="preview-value">{Math.ceil(1 / settings.pointValue)} points</span>
              </div>
              <div className="preview-item">
                <span className="preview-label">100 points value:</span>
                <span className="preview-value">${(100 * settings.pointValue).toFixed(2)}</span>
              </div>
              <div className="preview-item">
                <span className="preview-label">$100 spent earns:</span>
                <span className="preview-value">{Math.floor(100 / settings.currencyPerPoint) * settings.pointsPerCurrency} points</span>
              </div>
            </div>
          </div>
        </div>

        {/* Points Expiration */}
        <div className="loyalty-section">
          <h3>Points Expiration</h3>
          <p className="section-description">
            Configure whether points expire and after how long.
          </p>

          <div className="form-group toggle-group">
            <label className="toggle-label">
              <span>Points Expire</span>
              <div className="toggle-switch">
                <input
                  type="checkbox"
                  checked={settings.pointsExpire}
                  onChange={(e) => handleInputChange('pointsExpire', e.target.checked)}
                />
                <span className="toggle-slider"></span>
              </div>
            </label>
            <p className="field-help">
              When enabled, points will expire after the specified period from when they were earned.
            </p>
          </div>

          {settings.pointsExpire && (
            <div className="form-group expiration-months">
              <label>Expiration Period</label>
              <div className="inline-group">
                <input
                  type="number"
                  min="1"
                  max="60"
                  value={settings.expirationMonths}
                  onChange={(e) => handleInputChange('expirationMonths', parseInt(e.target.value) || 12)}
                  className="small-input"
                />
                <span>months from the transaction date</span>
              </div>
              <p className="field-help">
                Points will expire {settings.expirationMonths} month(s) after the "Paid At" date of the transaction where they were earned.
              </p>
            </div>
          )}
        </div>
        {/* Points Bonus */}
        <div className="loyalty-section">
          <h3>Points Bonus</h3>
          <p className="section-description">
            Set bonus multiplier periods where customers earn extra points on their transactions.
          </p>

          {/* Birthday Period */}
          <div className="bonus-period-block">
            <div className="form-group toggle-group">
              <label className="toggle-label">
                <span>Birthday Month Bonus</span>
                <div className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={settings.pointsBonus.birthday.enabled}
                    onChange={(e) => handleBonusChange('birthday', 'enabled', e.target.checked)}
                  />
                  <span className="toggle-slider"></span>
                </div>
              </label>
              <p className="field-help">
                Customers earn bonus points on transactions made during their birthday month.
              </p>
            </div>

            {settings.pointsBonus.birthday.enabled && (
              <div className="form-group inline-group bonus-multiplier-group">
                <label>Bonus Multiplier</label>
                <select
                  value={settings.pointsBonus.birthday.multiplier}
                  onChange={(e) => handleBonusChange('birthday', 'multiplier', parseInt(e.target.value))}
                  className="multiplier-select"
                >
                  <option value={2}>2X</option>
                  <option value={3}>3X</option>
                  <option value={4}>4X</option>
                  <option value={5}>5X</option>
                </select>
                <span className="field-help-inline">points relative to the usual earning rate</span>
              </div>
            )}
          </div>

          {/* Custom Period */}
          <div className="bonus-period-block">
            <div className="form-group toggle-group">
              <label className="toggle-label">
                <span>Custom Bonus Period</span>
                <div className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={settings.pointsBonus.custom.enabled}
                    onChange={(e) => handleBonusChange('custom', 'enabled', e.target.checked)}
                  />
                  <span className="toggle-slider"></span>
                </div>
              </label>
              <p className="field-help">
                Set a specific date range during which customers earn bonus points.
              </p>
            </div>

            {settings.pointsBonus.custom.enabled && (
              <div className="bonus-custom-fields">
                <div className="form-group inline-group">
                  <label>Start Date</label>
                  <input
                    type="date"
                    value={settings.pointsBonus.custom.startDate}
                    onChange={(e) => handleBonusChange('custom', 'startDate', e.target.value)}
                    className="date-input"
                  />
                </div>
                <div className="form-group inline-group">
                  <label>End Date</label>
                  <input
                    type="date"
                    value={settings.pointsBonus.custom.endDate}
                    onChange={(e) => handleBonusChange('custom', 'endDate', e.target.value)}
                    className="date-input"
                  />
                </div>
                <div className="form-group inline-group bonus-multiplier-group">
                  <label>Bonus Multiplier</label>
                  <select
                    value={settings.pointsBonus.custom.multiplier}
                    onChange={(e) => handleBonusChange('custom', 'multiplier', parseInt(e.target.value))}
                    className="multiplier-select"
                  >
                    <option value={2}>2X</option>
                    <option value={3}>3X</option>
                    <option value={4}>4X</option>
                    <option value={5}>5X</option>
                  </select>
                  <span className="field-help-inline">points relative to the usual earning rate</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="loyalty-actions">
        <button
          className="save-button"
          onClick={handleSave}
          disabled={saving || tierErrors.some(e => !e.isWarning)}
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>
    </div>
  );
}

export default LoyaltyProgramEditor;
